import { ArrowUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Image,
  Textarea,
} from '@chakra-ui/react';
import { FC, MutableRefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import { useSendMessageMutation } from 'rtkQuery/sharespro/queries/chat';
import attachIcon from 'assets/img/icons/attach.svg';
import getChatClipboardImages from 'utils/getChatClipboardImages';

import {
  ButtonSize,
  ButtonVariant,
} from 'presentation/theme/components/button';

interface Props {
  chatId: ID;
  scrollEndRef: MutableRefObject<HTMLDivElement | null>;
  isDisabled?: boolean;
  openFilePicker: () => void;
  onImagesPaste: (files: File[]) => void;
}

const ChatDetailControl: FC<Props> = ({
  chatId,
  scrollEndRef,
  isDisabled = false,
  openFilePicker,
  onImagesPaste,
}) => {
  const { t } = useTranslation();

  const [sendMessage] = useSendMessageMutation();

  const [messageText, setMessageText] = useState<string>('');

  const sendMessageCallback = (): void => {
    const messageTextValue = messageText.trim();
    if (messageTextValue) {
      setMessageText('');
      sendMessage({
        message: messageText,
        chat_id: chatId,
      });
      queueMicrotask(() => {
        scrollEndRef.current?.scrollIntoView();
      });
    }
  };

  return (
    <>
      <Box>
        <Divider />
        <Flex p="20px" alignItems="center">
          <Button
            px="5px"
            isDisabled={isDisabled}
            size={ButtonSize.md}
            onClick={() => {
              openFilePicker();
            }}
            variant={ButtonVariant.Ghost}
          >
            <Image src={attachIcon} h="20px" />
          </Button>
          <Box flex="1">
            <Textarea
              border="none"
              _focus={{
                border: 'none',
              }}
              _focusVisible={{
                border: 'none',
              }}
              disabled={isDisabled}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onPaste={(e) => {
                if (isDisabled) return;

                const images = getChatClipboardImages(e.clipboardData);

                if (images.length > 0) {
                  e.preventDefault();
                  onImagesPaste(images);
                }
              }}
              onKeyDown={(e) => {
                if (
                  e.key === 'Enter' &&
                  !e.shiftKey &&
                  !e.altKey &&
                  !e.ctrlKey &&
                  !e.metaKey
                ) {
                  e.preventDefault();
                  sendMessageCallback();
                }
              }}
              resize="none"
              minH="unset"
              as={TextareaAutosize}
              maxRows={6}
              minRows={1}
              placeholder={t('chatMessagePlaceholder') ?? undefined}
            />
          </Box>
          <IconButton
            w="40px"
            h="40px"
            rounded="50%"
            aria-label={t('sendMessage')}
            icon={<ArrowUpIcon w="24px" h="24px" />}
            onClick={sendMessageCallback}
            disabled={isDisabled || !messageText.trim()}
          />
        </Flex>
      </Box>
    </>
  );
};

export default ChatDetailControl;
